import React, { useEffect, useState } from 'react';

import { ApolloProvider, ApolloProvider as ApolloHooksProvider } from '@apollo/client';
import { useMatomo } from '@datapunt/matomo-tracker-react';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { ThemeProvider } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';

import AuthContext from './AuthContext';
import Loading from './components/Loading/Loading';
import ServiceWorkerWrapper from './components/ServiceWorker/ServiceWorkerWrapper';
import DefaultLayout from './containers/layouts/DefaultLayout';
import { getTranslationKey } from './i18n';
import MatomoContext from './MatomoContext';
import { legacyTheme, theme } from './muiTheme';
import client from './services/App/Client';
import appRouter from './services/AppRouter/AppRouter';
import auth from './services/Auth';
import IntermediaryBusinessConfigDialog from './views/IntermediaryBusiness/AutoActivationDialog';
import { Candidate, Login } from './views/Pages';

import '@coreui/icons/css/free.min.css';
import '@fortawesome/fontawesome/styles.css';
import '@fortawesome/fontawesome-svg-core/styles.css';
import 'animate.css/animate.min.css';
import 'flag-icon-css/css/flag-icon.min.css'; // Import Flag Icons Set
import 'font-awesome/css/font-awesome.min.css';
import 'moment/locale/fr';
import 'simple-line-icons/css/simple-line-icons.css';
import './scss/main.css';
import './i18n';

Moment.globalLocale = 'fr';

function App() {
  const { i18n } = useTranslation();
  const [isAuthenticated, setAuthenticated] = useState(auth.isAuthenticated());
  const [matomo, setMatomo] = useState(null);

  const [isAuthenticating, setAuthenticating] = useState(!auth.isAuthenticated());
  const [currentPath, setCurrentPath] = useState(appRouter.getHistory().location.pathname);
  const { pushInstruction } = useMatomo();
  const authContextValues = {
    isAuthenticated,
    setAuthenticated,
    isAuthenticating,
  };

  const { trackPageView } = useMatomo();

  useEffect(() => {
    setCurrentPath(appRouter.getHistory().location.pathname);

    if (isAuthenticated || currentPath.indexOf('/login') !== -1) {
      console.log('App - Authenticated or in /login, doing nothing');
      setAuthenticating(false);

      return;
    }

    if (!auth.hasNextUrl() && currentPath.indexOf('/login') === -1 && currentPath !== '/') {
      console.log('storeNextUrl');
      auth.storeNextUrl(currentPath);
    }

    // Try to authenticate from local storage
    console.log('App - Authenticating user from local storage.');
    auth
      .handleAuthentication({ logoutIfNoUserFound: true })
      .then(authenticated => {
        setAuthenticated(authenticated);
        setAuthenticating(false);

        if (!authenticated) {
          console.warn('App - No auto authentication performed from localstorage.');

          if (currentPath.indexOf('/login') === -1) {
            console.log("Not in /login route and not authenticated... let's redirect to /login");
            console.log('appRouter.getHistory().location', appRouter.getHistory().location);

            // Not in /login route and not authenticated... let's redirect to /login
            appRouter.goTo('/login');
          }
        } else {
          // no need for the nextUrl
          console.log('app - authenticated');

          const authenticatedContact = auth.getContact();
          const isCandidate = authenticatedContact && authenticatedContact.isCandidate;

          if (auth.hasNextUrl() && !isCandidate) {
            console.log('has next url', auth.getNextUrl());
            if (appRouter.getHistory().location.pathname === auth.getNextUrl()) {
              console.log('same as path, reseting');
              auth.resetNextUrl();
            } else {
              console.log('redirecting');
              auth.redirectToNextUrl();
            }
          }
        }
      })
      .catch(e => {
        console.error('App - Error while authenticating user from local storage.', e);
        setAuthenticating(false);
        setAuthenticated(false);
        auth.logout({ redirectToLogin: true });
      });
  }, [isAuthenticated, currentPath, trackPageView]);

  const authenticatedContact = auth.getContact();
  const isCandidate = authenticatedContact && authenticatedContact.isCandidate;

  useEffect(() => {
    if (isAuthenticated) {
      try {
        let userBU = auth.getContactInfo()?.company || auth.findUserCompany();
        if (!userBU) return;
        i18n.changeLanguage(getTranslationKey(userBU.code));
      } catch (error) {
        console.log('I18n init : Error while finding user company. Fallback to default translation');
      }

      const contactInfo = auth.getContactInfo();
      const userBU = contactInfo?.company || auth.findUserCompany();
      let role = 'UNDEFINED';
      const authenticatedContact = auth.getContact();
      if (authenticatedContact) {
        if (authenticatedContact.isCandidate) {
          role = 'CANDIDATE';
        } else if (authenticatedContact.isStaff) {
          role = 'STAFF';
        } else if (authenticatedContact.isAgent) {
          role = auth.getRoleLeader();
        }
      }
      pushInstruction('setUserId', authenticatedContact.email);
      pushInstruction('setCustomDimension', 1, userBU.name);
      pushInstruction('setCustomDimension', 2, role);
      setMatomo({
        userId: authenticatedContact.email,
        customDimension: [userBU.name, role],
      });
    } else {
      pushInstruction('setUserId', 'unauthenticated');
      pushInstruction('deleteCustomDimension', 1);
      pushInstruction('deleteCustomDimension', 2);
      setMatomo({
        userId: 'unauthenticated',
        customDimension: [],
      });
    }
  }, [i18n, isAuthenticated, pushInstruction]);

  return (
    <AuthContext.Provider value={authContextValues}>
      <MatomoContext.Provider value={matomo}>
        <ApolloProvider client={client}>
          <ApolloHooksProvider client={client}>
            <MuiThemeProvider theme={legacyTheme}>
              <ThemeProvider theme={theme}>
                <ServiceWorkerWrapper />
                <IntermediaryBusinessConfigDialog />
                {isAuthenticating && <Loading type={Loading.TYPE.DIGIT_FULLSCREEN} />}
                <DndProvider backend={HTML5Backend}>
                  <Router history={appRouter.getHistory()}>
                    <Switch>
                      <Route exact path={'/login'} name={'Login Page'} component={Login} />
                      <Route exact path={'/login/failed/:error_message?'} name={'Login Page'} component={Login} />

                      {isAuthenticated ? (
                        isCandidate ? (
                          <>
                            <Route exact path={'/candidate'} name={'Page candidat'} component={Candidate} />
                            <Redirect from={'*'} to={'/candidate'} />
                          </>
                        ) : (
                          <>
                            <Route path={'/'} name={'Dashboard'} component={DefaultLayout} />
                          </>
                        )
                      ) : (
                        !isAuthenticating && <Redirect from={'*'} to={'/login'} />
                      )}
                    </Switch>

                    <ToastContainer />
                  </Router>
                </DndProvider>
              </ThemeProvider>
            </MuiThemeProvider>
          </ApolloHooksProvider>
        </ApolloProvider>
      </MatomoContext.Provider>
    </AuthContext.Provider>
  );
}

export default React.memo(App);
